import React, { useState, useEffect, useMemo, useRef } from "react";
import { List as ListEntity, Section as SectionEntity, ListItem as ListItemEntity } from "@/entities/all";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Settings, Filter, FileText, Pin, PinOff, Eye, EyeOff } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { createPageUrl, getAllAncestorListIds } from "@/utils";
import { useData } from "../components/DataContext";
import { groupBy } from 'lodash';
import { motion, AnimatePresence } from 'framer-motion';

import ListHeader from "../components/list/ListHeader";
import SectionComponent from "../components/list/SectionComponent";
import InheritedBy from "../components/list/InheritedBy";
import ListSettings from "../components/list/ListSettings";
import AuthLoader from "../components/auth/AuthLoader";
import SignInRequired from "../components/auth/SignInRequired";
import ExportListDialog from "../components/list/ExportListDialog";
import type { List, Section, ListItem } from "@/types";

export default function EditList() {
  const navigate = useNavigate();
  const { folders, lists, sections, listItems, inheritedCompletions, isLoading, runOptimisticUpdate, setOptimisticData, refreshData, user, userLoading } = useData();

  const [list, setList] = useState<List | null>(null);
  const [showSettings, setShowSettings] = useState(false);
  const [filterUnselected, setFilterUnselected] = useState(false);
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [isPinned, setIsPinned] = useState(true);
  const [showOnlyOriginal, setShowOnlyOriginal] = useState(false);
  const hasRunCleanupRef = useRef(false);
  
  const urlParams = new URLSearchParams(window.location.search);
  const listId = urlParams.get('id');
  
  useEffect(() => {
    const currentList = lists.find(l => l.id === listId) || null;
    setList(currentList);
  }, [listId, lists]);
  
  useEffect(() => {
    hasRunCleanupRef.current = false;
  }, [listId]);

  // Auto-create missing inherited sections on mount (once per list)
  useEffect(() => {
    if (!list || !listId || isLoading || hasRunCleanupRef.current) return;

    const performCreation = async () => {
      hasRunCleanupRef.current = true;

      const ancestorListIds = getAllAncestorListIds(listId, lists);
      if (ancestorListIds.length === 0) return;

      const currentSections = sections;

      const inheritedSectionTitles: string[] = [];
      ancestorListIds.forEach(inheritedId => {
        const inheritedSections = currentSections.filter(s => s.list_id === inheritedId);
        inheritedSections.forEach(s => {
          const title = (s.title || '').trim();
          if (title && !inheritedSectionTitles.includes(title)) {
            inheritedSectionTitles.push(title);
          }
        });
      });

      const nativeSectionTitles = currentSections
        .filter(s => s.list_id === listId)
        .map(s => (s.title || '').trim());

      const missingSectionTitles = inheritedSectionTitles.filter(
        title => !nativeSectionTitles.includes(title)
      );

      if (missingSectionTitles.length > 0) {
        const currentMaxPosition = currentSections.filter(s => s.list_id === listId).length;
        await Promise.all(
          missingSectionTitles.map((title, index) =>
            SectionEntity.create({
              list_id: listId,
              title: title,
              position: currentMaxPosition + index
            })
          )
        );
        await refreshData();
      }
    };

    performCreation();
  }, [list, listId, isLoading, refreshData, sections, lists]);

  const mergedSections: Section[] = useMemo(() => {
    if (!list || !listId) return [];
    
    // 1. Get current list's sections and items
    const nativeSections = sections
      .filter(s => s.list_id === listId)
      .map(s => ({
        ...s,
        items: listItems
          .filter(i => i.section_id === s.id)
          .sort((a, b) => (a.position || 0) - (b.position || 0))
          .map(i => ({ ...i, isInherited: false }))
      }));

    // 2. Get inherited sections and items (recursively from all ancestor lists)
    const ancestorListIds = getAllAncestorListIds(listId, lists);
    const inheritedSectionsWithItems = ancestorListIds.flatMap(ancestorId => {
      const ancestorList = lists.find(l => l.id === ancestorId);
      if (!ancestorList) return [];

      return sections
        .filter(s => s.list_id === ancestorId)
        .map(s => ({
          ...s,
          items: listItems
            .filter(i => i.section_id === s.id)
            .sort((a, b) => (a.position || 0) - (b.position || 0))
            .map(i => ({
              ...i,
              isInherited: true,
              sourceListTitle: ancestorList.title,
              sourceListId: ancestorList.id
            }))
        }));
    });

    // 3. Merge sections by title
    const allSections = [...nativeSections, ...inheritedSectionsWithItems];
    const groupedByTitle = groupBy(allSections, section => (section.title || '').trim());
    
    const seenItemIds = new Set<string>();

    const finalMergedSections: Section[] = Object.entries(groupedByTitle).map(([title, sectionGroup]) => {
      const baseSection = sectionGroup.find(s => s.list_id === listId) || sectionGroup[0];
      const allItems: ListItem[] = [];

      for (const sec of sectionGroup) {
        for (const it of (sec.items || [])) {
          if (!seenItemIds.has(it.id)) {
            seenItemIds.add(it.id);
            allItems.push(it);
          }
        }
      }

      allItems.sort((a, b) => (a.position || 0) - (b.position || 0));

      return {
        ...baseSection,
        title: title || null,
        items: allItems,
      };
    });

    return finalMergedSections.sort((a, b) => (a.position || 0) - (b.position || 0));
  }, [list, sections, listItems, lists, listId]);

  // Filter sections based on showOnlyOriginal
  const displayedSections = useMemo(() => {
    if (!showOnlyOriginal || !listId) return mergedSections;
    
    return mergedSections
      .filter(section => section.list_id === listId)
      .map(section => ({
        ...section,
        items: (section.items || []).filter(item => !item.isInherited)
      }));
  }, [mergedSections, showOnlyOriginal, listId]);

  const isItemCompletedHelper = (item: ListItem): boolean => {
    if (item.isInherited && listId) {
      const serverComp = inheritedCompletions?.find(c => c.list_id === listId && c.item_id === item.id);
      if (serverComp !== undefined) return serverComp.completed;
      const inheritanceKey = `${item.id}_${listId}`;
      try {
        const states = JSON.parse(localStorage.getItem('inheritedItemStates') || '{}');
        return Boolean(states[inheritanceKey]);
      } catch {
        return false;
      }
    }
    return item.completed;
  };

  const handleSelectAll = async () => {
    const allItems = mergedSections.flatMap(section => 
      (section.items || []).filter(item => !isItemCompletedHelper(item))
    );
    
    if (allItems.length === 0 || !listId) return;
    
    const nativeItems = allItems.filter(item => !item.isInherited);
    const inheritedItems = allItems.filter(item => item.isInherited);
    
    if (nativeItems.length > 0) {
      const originalState = { listItems: [...listItems] };
      const task = async () => {
        await ListEntity.setItemsCompleted(listId, true);
      };
      
      const newState = {
        folders: [...folders],
        lists: [...lists],
        sections: [...sections],
        listItems: listItems.map(item => 
          nativeItems.find(nativeItem => nativeItem.id === item.id)
            ? { ...item, completed: true }
            : item
        )
      };

      runOptimisticUpdate(task, { update: newState, originalState: { folders, lists, sections, ...originalState } });
    }
    
    if (inheritedItems.length > 0) {
      try {
        const currentState = JSON.parse(localStorage.getItem('inheritedItemStates') || '{}');
        inheritedItems.forEach(item => {
          const inheritanceKey = `${item.id}_${listId}`;
          currentState[inheritanceKey] = true;
          ListEntity.setInheritedItemCompleted(listId, item.id, true).catch(() => {});
        });
        localStorage.setItem('inheritedItemStates', JSON.stringify(currentState));
        setOptimisticData(prevData => ({ ...prevData }));
      } catch (e) {}
    }
  };

  const handleDeselectAll = async () => {
    const allCompletedItems = mergedSections.flatMap(section => 
      (section.items || []).filter(item => isItemCompletedHelper(item))
    );
    
    if (allCompletedItems.length === 0 || !listId) return;
    
    const nativeItems = allCompletedItems.filter(item => !item.isInherited);
    const inheritedItems = allCompletedItems.filter(item => item.isInherited);
    
    if (nativeItems.length > 0) {
      const originalState = { listItems: [...listItems] };
      const task = async () => {
        await ListEntity.setItemsCompleted(listId, false);
      };
      
      const newState = {
        folders: [...folders],
        lists: [...lists],
        sections: [...sections],
        listItems: listItems.map(item => 
          nativeItems.find(completedItem => completedItem.id === item.id)
            ? { ...item, completed: false }
            : item
        )
      };

      runOptimisticUpdate(task, { update: newState, originalState: { folders, lists, sections, ...originalState } });
    }
    
    if (inheritedItems.length > 0) {
      try {
        const currentState = JSON.parse(localStorage.getItem('inheritedItemStates') || '{}');
        inheritedItems.forEach(item => {
          const inheritanceKey = `${item.id}_${listId}`;
          delete currentState[inheritanceKey];
          ListEntity.setInheritedItemCompleted(listId, item.id, false).catch(() => {});
        });
        localStorage.setItem('inheritedItemStates', JSON.stringify(currentState));
        setOptimisticData(prevData => ({ ...prevData }));
      } catch (e) {}
    }
  };
  
  const handleMoveSection = (sectionId: string, direction: 'up' | 'down') => {
    if (!listId) return;
    const nativeSections = sections
      .filter(s => s.list_id === listId)
      .sort((a, b) => (a.position || 0) - (b.position || 0));
    
    const index = nativeSections.findIndex(item => item.id === sectionId);
    if (index === -1) return;

    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= nativeSections.length) return;

    const originalState = { sections: [...sections] };
    const itemA = nativeSections[index];
    const itemB = nativeSections[newIndex];
    
    const task = async () => {
      await Promise.all([
        SectionEntity.update(itemA.id, { position: newIndex }),
        SectionEntity.update(itemB.id, { position: index })
      ]);
    };
    
    const newState = {
      folders: [...folders],
      lists: [...lists],
      sections: sections.map(section => {
        if (section.id === itemA.id) return { ...section, position: newIndex };
        if (section.id === itemB.id) return { ...section, position: index };
        return section;
      }),
      listItems: [...listItems]
    };

    setOptimisticData(newState);
    runOptimisticUpdate(task, { update: newState, originalState: { folders, lists, sections, listItems } });
  };
  
  const handleAddSection = () => {
    if (!listId) return;
    const originalState = { sections: [...sections] };
    const newSectionId = `temp-${Date.now()}`;
    const newPosition = sections.filter(s => s.list_id === listId).length;

    const task = async () => {
      const newSection = await SectionEntity.create({ list_id: listId, position: newPosition });
      setOptimisticData(prevData => ({
        ...prevData,
        sections: prevData.sections.map(s =>
          s.id === newSectionId ? { ...s, ...newSection } : s
        )
      }));
      setTimeout(() => {
        const titleInput = document.querySelector(`[data-section-id="${newSection.id}"] input`) as HTMLInputElement | null;
        if (titleInput) titleInput.focus();
      }, 0);
    };

    const newState = {
      folders: [...folders],
      lists: [...lists],
      sections: [...sections, { id: newSectionId, list_id: listId, title: '', items: [], position: newPosition } as Section],
      listItems: [...listItems]
    };

    runOptimisticUpdate(task, { update: newState, originalState: { folders, lists, ...originalState, listItems } });
  };

  if (userLoading) {
    return <AuthLoader />;
  }

  if (!user) {
    return <SignInRequired />;
  }

  if (isLoading && !list) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="animate-pulse text-slate-600">Loading...</div>
      </div>
    );
  }

  if (!list) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <p className="text-slate-600 mb-4">List not found or an error occurred.</p>
          <Button onClick={() => navigate(createPageUrl("Dashboard"))}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  const allNativeItems = mergedSections.flatMap(section => 
    (section.items || []).filter(item => !item.isInherited)
  );
  const allInheritedItems = mergedSections.flatMap(section => 
    (section.items || []).filter(item => item.isInherited)
  );
  
  const completedNativeItems = allNativeItems.filter(item => item.completed);
  const incompleteNativeItems = allNativeItems.filter(item => !item.completed);
  
  const completedInheritedItems = allInheritedItems.filter(item => isItemCompletedHelper(item));
  const incompleteInheritedItems = allInheritedItems.filter(item => !isItemCompletedHelper(item));
  
  const totalIncompleteItems = incompleteNativeItems.length + incompleteInheritedItems.length;
  const totalCompletedItems = completedNativeItems.length + completedInheritedItems.length;
  const totalItems = allNativeItems.length + allInheritedItems.length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <div className="max-w-4xl mx-auto p-2 sm:p-4 md:p-8">
        {/* Header */}
        <div className="flex flex-col gap-4 mb-6 md:mb-8">
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              size="icon"
              onClick={() => navigate(createPageUrl("Dashboard"))}
              className="border-slate-200 hover:bg-slate-50 flex-shrink-0"
            >
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div className="flex-1 min-w-0">
              <ListHeader list={list} />
            </div>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
            {/* Select All/Deselect All Buttons */}
            {totalItems > 0 && (
              <div className="flex gap-2 flex-1">
                {totalIncompleteItems > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleSelectAll}
                    className="flex-1 sm:flex-none border-slate-200 hover:bg-green-50 hover:border-green-300 hover:text-green-700 text-xs sm:text-sm"
                  >
                    Select All ({totalIncompleteItems})
                  </Button>
                )}
                {totalCompletedItems > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleDeselectAll}
                    className="flex-1 sm:flex-none border-slate-200 hover:bg-orange-50 hover:border-orange-300 hover:text-orange-700 text-xs sm:text-sm"
                  >
                    Deselect All ({totalCompletedItems})
                  </Button>
                )}
              </div>
            )}
            
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowOnlyOriginal(prev => !prev)}
                className={`border-slate-200 hover:bg-slate-50 flex-shrink-0 ${showOnlyOriginal ? 'bg-purple-50 border-purple-300 text-purple-700' : ''}`}
              >
                {showOnlyOriginal ? <Eye className="w-4 h-4 sm:mr-2" /> : <EyeOff className="w-4 h-4 sm:mr-2" />}
                <span className="hidden sm:inline">{showOnlyOriginal ? 'Original Only' : 'All Items'}</span>
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsPinned(prev => !prev)}
                className={`border-slate-200 hover:bg-slate-50 flex-shrink-0 ${isPinned ? 'bg-blue-50 border-blue-300 text-blue-700' : ''}`}
              >
                {isPinned ? <Pin className="w-4 h-4 sm:mr-2" /> : <PinOff className="w-4 h-4 sm:mr-2" />}
                <span className="hidden sm:inline">{isPinned ? 'Pinned' : 'Unpinned'}</span>
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setFilterUnselected(prev => !prev)}
                className="border-slate-200 hover:bg-slate-50 flex-1 sm:flex-none"
              >
                <Filter className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">{filterUnselected ? 'Show All' : 'Filter Unselected'}</span>
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowExportDialog(true)}
                className="border-slate-200 hover:bg-slate-50 flex-shrink-0"
              >
                <FileText className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Export</span>
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowSettings(true)}
                className="border-slate-200 hover:bg-slate-50 flex-shrink-0"
              >
                <Settings className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Settings</span>
              </Button>
            </div>
          </div>
        </div>

        {/* Main List Sections */}
        <div className="space-y-4 md:space-y-6">
          <AnimatePresence>
            {displayedSections.map((section) => {
              const nativeSections = displayedSections.filter(s => s.list_id === listId);
              const nativeIndex = nativeSections.findIndex(s => s.id === section.id);
              return (
                <motion.div
                  key={section.id}
                  layout
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                >
                  <SectionComponent
                    section={section}
                    listId={list.id}
                    onMove={handleMoveSection}
                    index={nativeIndex}
                    totalItems={nativeSections.length}
                    allLists={lists}
                    filterUnselected={filterUnselected}
                    isPinned={isPinned}
                  />
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>

        {/* Add Section Button */}
        <div className="mt-6 md:mt-8 flex justify-center">
          <Button
            variant="outline"
            onClick={handleAddSection}
            className="border-slate-200 hover:bg-slate-50"
          >
            Add Section
          </Button>
        </div>

        <InheritedBy listId={list.id} allLists={lists} />
      </div>

      <ListSettings
        list={list}
        open={showSettings}
        onClose={() => setShowSettings(false)}
        onDelete={() => navigate(createPageUrl("Dashboard"))}
      />

      <ExportListDialog
        open={showExportDialog}
        onClose={() => setShowExportDialog(false)}
        list={list}
        sections={showOnlyOriginal ? displayedSections : mergedSections}
      />
    </div>
  );
}
